import React, { useState, useEffect, useImperativeHandle, forwardRef, memo } from 'react';
import {
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  Text,
  Divider,
  Checkbox,
  Textarea,
  Button,
} from '@chakra-ui/react';

/**
 * FaxFormFields - Isolated form fields component that manages its own state
 * 
 * This prevents the massive parent component from re-rendering on every keystroke.
 * Values are exposed via ref.getFormData() for use when submitting.
 */
const FaxFormFields = memo(forwardRef(({
  srfaxAuthEmails = [],
  isDisabled = false,
  onSubmit,
  submitButtonText = 'Send Fax',
  isSubmitting = false,
  canSubmit = true,
  // Initial values for pre-populating (e.g., from contact selection)
  initialFaxNumber = '1',
  initialRecipientName = '',
}, ref) => {
  // All form state is managed locally
  const [faxNumber, setFaxNumber] = useState(initialFaxNumber);
  const [recipientName, setRecipientName] = useState(initialRecipientName);
  const [senderName, setSenderName] = useState('');
  const [senderFax, setSenderFax] = useState('');
  const [senderEmail, setSenderEmail] = useState(srfaxAuthEmails.length > 0 ? srfaxAuthEmails[0] : '');
  const [subject, setSubject] = useState('');
  const [coverPage, setCoverPage] = useState(false);
  const [coverPageText, setCoverPageText] = useState('');

  // Sync initial values when they change (e.g., contact selection)
  useEffect(() => {
    setFaxNumber(initialFaxNumber);
  }, [initialFaxNumber]);

  useEffect(() => {
    setRecipientName(initialRecipientName);
  }, [initialRecipientName]);

  // Update email when auth emails change
  useEffect(() => {
    if (srfaxAuthEmails.length > 0 && !senderEmail) {
      setSenderEmail(srfaxAuthEmails[0]);
    }
  }, [srfaxAuthEmails]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    getFormData: () => ({
      fax_number: faxNumber,
      recipient_name: recipientName,
      sender_name: senderName,
      sender_fax: senderFax,
      sender_email: senderEmail,
      subject: subject,
      cover_page: coverPage,
      cover_page_text: coverPageText,
    }),
    setFaxNumber: (val) => setFaxNumber(val),
    setRecipientName: (val) => setRecipientName(val),
    resetForm: () => {
      setFaxNumber('1');
      setRecipientName('');
      setSenderName('');
      setSenderFax('');
      setSenderEmail(srfaxAuthEmails.length > 0 ? srfaxAuthEmails[0] : '');
      setSubject('');
      setCoverPage(false);
      setCoverPageText('');
    },
    isValid: () => {
      return faxNumber.trim() !== '' && senderEmail.trim() !== '';
    },
  }));

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit({
        fax_number: faxNumber,
        recipient_name: recipientName,
        sender_name: senderName,
        sender_fax: senderFax,
        sender_email: senderEmail,
        subject: subject,
        cover_page: coverPage,
        cover_page_text: coverPageText,
      });
    }
  };

  return (
    <VStack spacing={4} align="stretch">
      {/* Recipient Information */}
      <FormControl isRequired>
        <FormLabel>Recipient Fax Number</FormLabel>
        <Input
          value={faxNumber}
          onChange={(e) => setFaxNumber(e.target.value)}
          isDisabled={isDisabled}
          placeholder="1 (US country code - edit for international)"
        />
        <Text fontSize="xs" color="gray.500" mt={1}>
          Accepts formats like: 1(213) 260-2301, 1213-260-2301, 12132602301, etc.
        </Text>
      </FormControl>

      <FormControl>
        <FormLabel>Recipient Name (Optional)</FormLabel>
        <Input
          placeholder="Recipient name"
          value={recipientName}
          onChange={(e) => setRecipientName(e.target.value)}
          isDisabled={isDisabled}
        />
      </FormControl>

      <Divider />

      {/* Sender Information */}
      <Text fontSize="md" fontWeight="bold">
        Sender Information (Optional)
      </Text>

      <FormControl>
        <FormLabel>Sender Name</FormLabel>
        <Input
          placeholder="Your name or organization"
          value={senderName}
          onChange={(e) => setSenderName(e.target.value)}
          isDisabled={isDisabled}
        />
      </FormControl>

      <FormControl>
        <FormLabel>Sender Fax Number</FormLabel>
        <Input
          placeholder="Your fax number"
          value={senderFax}
          onChange={(e) => setSenderFax(e.target.value)}
          isDisabled={isDisabled}
        />
      </FormControl>

      <FormControl isRequired>
        <FormLabel>Sender Email (Required)</FormLabel>
        {srfaxAuthEmails.length > 0 ? (
          <Select
            value={senderEmail || (srfaxAuthEmails.length > 0 ? srfaxAuthEmails[0] : '')}
            onChange={(e) => setSenderEmail(e.target.value)}
            isDisabled={isDisabled}
          >
            {srfaxAuthEmails.map((email) => (
              <option key={email} value={email}>
                {email}
              </option>
            ))}
          </Select>
        ) : (
          <Input
            type="email"
            placeholder="your@email.com (must be authorized in SRFax)"
            value={senderEmail}
            onChange={(e) => setSenderEmail(e.target.value)}
            isDisabled={isDisabled}
          />
        )}
        <Text fontSize="xs" color="gray.500" mt={1}>
          Required by SRFax API. Must be authorized in your SRFax account.
        </Text>
      </FormControl>

      <FormControl>
        <FormLabel>Subject (Optional)</FormLabel>
        <Input
          placeholder="Fax subject line"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          isDisabled={isDisabled}
        />
      </FormControl>

      <Divider />

      {/* Cover Page Options */}
      <FormControl>
        <Checkbox
          isChecked={coverPage}
          onChange={(e) => setCoverPage(e.target.checked)}
          isDisabled={isDisabled}
        >
          Include Cover Page
        </Checkbox>
      </FormControl>

      {coverPage && (
        <FormControl>
          <FormLabel>Cover Page Text (Optional)</FormLabel>
          <Textarea
            rows={4}
            placeholder="Enter cover page message..."
            value={coverPageText}
            onChange={(e) => setCoverPageText(e.target.value)}
            isDisabled={isDisabled}
          />
        </FormControl>
      )}

      {/* Send Button */}
      <Button
        colorScheme="green"
        onClick={handleSubmit}
        isLoading={isSubmitting}
        loadingText="Sending..."
        isDisabled={
          !canSubmit || 
          !faxNumber.trim() ||
          !senderEmail.trim() ||
          isDisabled
        }
        size="lg"
      >
        {submitButtonText}
      </Button>
    </VStack>
  );
}));

FaxFormFields.displayName = 'FaxFormFields';

export default FaxFormFields;
